/**
 * Utility functions for table pagination and sorting management
 */

/**
 * Calculates the appropriate page number after items are deleted
 * 
 * @param {number} deletedCount - Number of items being deleted
 * @param {number} currentPage - Current page number
 * @param {number} totalPages - Total number of pages before deletion
 * @param {number} itemsPerPage - Number of items per page
 * @param {number} totalItems - Total number of items before deletion
 * @returns {number} - The page number to navigate to after deletion
 */
export const calculatePageAfterDeletion = (deletedCount, currentPage, totalPages, itemsPerPage, totalItems) => {
    const remainingItems = totalItems - deletedCount;
    const newTotalPages = Math.ceil(remainingItems / itemsPerPage);

    // If current page is beyond the new total pages, go to the last page
    if (currentPage > newTotalPages && newTotalPages > 0) {
        return newTotalPages;
    }

    // If we're on the last page and it becomes empty, go to the previous page
    if (currentPage === totalPages && remainingItems % itemsPerPage === 0 && currentPage > 1) {
        return currentPage - 1;
    }

    // Otherwise, stay on the same page
    return currentPage;
};

/**
 * Calculates pagination values for a given dataset
 * 
 * @param {Array} items - Array of items to paginate
 * @param {number} currentPage - Current page number (1-based)
 * @param {number} itemsPerPage - Number of items per page
 * @returns {Object} - Object containing pagination values
 */
export const calculatePagination = (items, currentPage, itemsPerPage) => {
    const totalPages = Math.ceil(items.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentItems = items.slice(startIndex, endIndex);

    return {
        totalPages,
        startIndex,
        endIndex,
        currentItems
    };
};

/**
 * Creates a sorting function for table data
 * 
 * @param {string} sortField - Field to sort by
 * @param {string} sortDirection - 'asc' or 'desc'
 * @param {Object} fieldConfig - Configuration for how to sort each field
 * @returns {Function} - Sorting function
 */
export const createSortFunction = (sortField, sortDirection, fieldConfig) => {
    return (a, b) => {
        const config = fieldConfig[sortField];
        if (!config) return 0;

        let aValue, bValue;

        if (config.type === 'date') {
            aValue = new Date(config.getValue(a));
            bValue = new Date(config.getValue(b));
        } else if (config.type === 'string') {
            aValue = (config.getValue(a) || '').toLowerCase();
            bValue = (config.getValue(b) || '').toLowerCase();
        } else if (config.type === 'number') {
            aValue = config.getValue(a);
            bValue = config.getValue(b);
        } else {
            return 0;
        }

        // Handle null/undefined values
        if (config.nullHandling === 'bottom') {
            const aHasValue = aValue !== null && aValue !== undefined && aValue !== '';
            const bHasValue = bValue !== null && bValue !== undefined && bValue !== '';

            if (aHasValue && !bHasValue) return -1;
            if (!aHasValue && bHasValue) return 1;
            if (!aHasValue && !bHasValue) {
                // Both have no value, use fallback sorting
                if (config.fallback) {
                    return config.fallback(a, b);
                }
                return 0;
            }
        }

        if (aValue < bValue) {
            return sortDirection === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
            return sortDirection === 'asc' ? 1 : -1;
        }

        // If values are equal, use fallback sorting for stable ordering
        if (config.fallback) {
            return config.fallback(a, b);
        }

        return 0;
    };
};

/**
 * Creates a sort handler function for table headers
 * 
 * @param {string} currentSortField - Current sort field
 * @param {string} currentSortDirection - Current sort direction
 * @param {Function} setSortField - Function to set sort field
 * @param {Function} setSortDirection - Function to set sort direction
 * @param {Function} setCurrentPage - Function to reset to page 1
 * @param {Object} defaultSorts - Default sort directions for each field
 * @returns {Function} - Sort handler function
 */
export const createSortHandler = (
    currentSortField,
    currentSortDirection,
    setSortField,
    setSortDirection,
    setCurrentPage,
    defaultSorts = {}
) => {
    return (field) => {
        if (currentSortField === field) {
            setSortDirection(currentSortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection(defaultSorts[field] || 'asc');
        }
        setCurrentPage(1);
    };
};
